import { computed, readonly, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { NEVER } from 'rxjs';
import { cloneDeep } from 'lodash';

import { ItinerarySearchResults } from '@apps/main/modules/tripPlannerModel/itinerarySearchResults';
import { SmartSearchService } from '@apps/main/modules/search/smartSearchService';
import type {
  PriceCalendarState,
  PriceRibbonMonth,
} from '@apps/main/new-desktop/modules/calendars';
import { getPriceCalendarState } from '@apps/main/new-desktop/core/stores/priceCalendar/priceCalendarStoreLogic';
import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useGlobalCurrencyStore,
  usePaxStore,
  usePriceCalendarStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { adaptRecipeIngredientsToItinerarySearchCriteria } from '@apps/main/modules/criteria/itinerarySearchCriteria/itinerariesCriteria';
import type { DateWithItinerary } from '@apps/main/new-desktop/core/stores/trip/tripStore.model';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { getGuestRating } from '@apps/main/new-desktop/modules/customersRating';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import { assertValue } from '@libs/utils';
import type {
  ItinerarySearchCriteria,
  ItinerarySearchUserCriteria,
  OverviewStaticInfoResult,
} from '@libs/trip-planner-interfaces';

import type {
  PeekPriceModalCalendar,
  PeekPriceModalItineraryData,
} from '../models';
import { PeekPriceModalItineraryType } from '../models';

import {
  doesDateMatchAnyComparisonDate,
  getDateOfCheapestItinerary,
  getMonthStartAndEndDates,
} from './peekPriceStoreLogic';
import {
  getPeekPriceModalHotelOnlyItinerary,
  getPeekPriceModalBookableTransportPlusHotelItinerary,
  getPeekPriceModalMultiIngredientItinerary,
} from './selectedItineraryLogic';

const SEARCH_SERVICE = new SmartSearchService();

const { t } = i18n.global;

export const usePeekPriceStore = defineStore('PeekPriceHotel', () => {
  const criteriaStore = useCriteriaStore();
  const globalCurrencyStore = useGlobalCurrencyStore();
  const paxStore = usePaxStore();
  const preferredTimeStore = usePreferredTimeStore();
  const priceCalendarStore = usePriceCalendarStore();
  const recipeStore = useRecipeStore();
  const tripStore = useTripStore();

  // State
  const itineraryDatesRef = ref<DateWithItinerary[]>([]);
  const selectedMonthISODateRef = ref<string | undefined>(undefined);
  const ingredientIndexRef = ref<number | undefined>(undefined);
  const hotelCodeRef = ref<string | undefined>(undefined);
  const selectedDateRef = ref<Date | undefined>(undefined);
  const itineraryStaticInfo = ref<OverviewStaticInfoResult>({
    destinationStaticInfo: {},
    subIngredientsStaticInfo: {},
    heartedHotelsStaticInfo: {},
  });
  const peekPriceRecipe = ref<RecipeMainIngredient[] | undefined>(undefined);

  // Actions
  function loadPrices(ingredientIndex: number, hotelCode: string): void {
    itineraryDatesRef.value = [];
    ingredientIndexRef.value = ingredientIndex;
    hotelCodeRef.value = hotelCode;

    const recipeCopy: RecipeMainIngredient[] = cloneDeep(recipeStore.recipe);
    const ingredient = recipeCopy[ingredientIndex];

    if (!ingredient) {
      return;
    }

    if (!isHotelIngredient(ingredient)) {
      return;
    }

    ingredient.heartedHotels = [{ code: hotelCode }];
    peekPriceRecipe.value = recipeCopy;

    const dates = selectedMonthISODateRef.value
      ? getMonthStartAndEndDates(new Date(selectedMonthISODateRef.value))
      : {
          startDate: preferredTimeStore.startSearchDate,
          endDate: preferredTimeStore.endSearchDate,
          selectedDate:
            selectedDateRef.value ?? preferredTimeStore.preferredDay,
        };

    const userCriteria: ItinerarySearchUserCriteria = {
      ingredients: adaptRecipeIngredientsToItinerarySearchCriteria(recipeCopy),
      pax: paxStore.pax.rooms,
      endSearchDate: dates.endDate,
      startSearchDate: dates.startDate,
      selectedDate: dates.selectedDate,
      sessionId: tripStore.sessionId,
    };
    const criteria: ItinerarySearchCriteria = {
      userCriteria,
      businessCriteria: criteriaStore.businessCriteria,
    };
    const results = new ItinerarySearchResults(SEARCH_SERVICE, criteria, NEVER);
    results.itineraries.subscribe((i) => {
      const itinerary = { date: i.date, itinerary: i };
      const allItineraries = [...itineraryDatesRef.value, itinerary];
      allItineraries.sort((a, b) => a.date.getTime() - b.date.getTime());
      itineraryDatesRef.value = [...allItineraries];
    });

    results.staticInfo.subscribe((staticInfo) => {
      itineraryStaticInfo.value = {
        destinationStaticInfo: {
          ...itineraryStaticInfo.value.destinationStaticInfo,
          ...staticInfo.destinationStaticInfo,
        },
        subIngredientsStaticInfo: {
          ...itineraryStaticInfo.value.subIngredientsStaticInfo,
          ...staticInfo.subIngredientsStaticInfo,
        },
        heartedHotelsStaticInfo: {
          ...itineraryStaticInfo.value.heartedHotelsStaticInfo,
          ...staticInfo.heartedHotelsStaticInfo,
        },
      };
    });
  }

  function selectMonth(date: Date): void {
    const semboDate = new SemboDate(date);
    selectedMonthISODateRef.value = semboDate.format(DateFormat.ISO);
    selectedDateRef.value = undefined;
  }

  function preselectDate(date: Date): void {
    selectedDateRef.value = date;
    const preselectedItinerary = itineraryDatesRef.value.find(
      (itinerary) => itinerary.date === date
    );
    if (
      !preselectedItinerary?.itinerary?.isSearched &&
      ingredientIndexRef.value &&
      hotelCodeRef.value
    ) {
      loadPrices(ingredientIndexRef.value, hotelCodeRef.value);
    }
  }

  function clean(): void {
    itineraryDatesRef.value = [];
    selectedMonthISODateRef.value = undefined;
    ingredientIndexRef.value = undefined;
    hotelCodeRef.value = undefined;
    selectedDateRef.value = undefined;
  }

  function confirm(): void {
    if (ingredientIndexRef.value === undefined || !hotelCodeRef.value) {
      clean();
      return;
    }

    const heartedHotel = { code: hotelCodeRef.value };
    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;
    assertValue(ingredientId, 'peekPriceStore.ts > clean > ingredientId');
    tripStore.replaceHeartedHotel(heartedHotel, ingredientId);

    if (selectedMonthISODateRef.value && !selectedDateRef.value) {
      tripStore.toggleExpandedMonth(new Date(selectedMonthISODateRef.value));
    }

    if (selectedDateRef.value) {
      preferredTimeStore.setPreferredDay(selectedDateRef.value);
    }

    clean();
  }

  // Getters
  const months = computed<PriceRibbonMonth[]>(() => {
    if (!selectedMonthISODateRef.value) {
      return priceCalendarStore.priceRibbonMonths;
    }

    return priceCalendarStore.priceRibbonMonths.map(
      (month: PriceRibbonMonth) => {
        const isExtended =
          new SemboDate(month.firstDayInMonthISO).format(
            DateFormat.ShortMonthAndYear
          ) ===
          new SemboDate(selectedMonthISODateRef.value).format(
            DateFormat.ShortMonthAndYear
          );

        return { ...month, isExtended };
      }
    );
  });

  const selectedMonth = computed<string>(
    () =>
      selectedMonthISODateRef.value ?? priceCalendarStore.selectedMonthOption.id
  );

  const priceCalendarState = computed<PriceCalendarState>(() =>
    getPriceCalendarState({
      weekdaysFormat: DateFormat.ShortDayName,
      itineraryDates: itineraryDatesRef.value,
      alternativeProductMessage: t('planner.alternative_avail'),
      noAvailabilityMessage: t('planner.no_avail'),
      forceLoader: isFeatureToggleActive('loader'),
      moneyFormanterFn: globalCurrencyStore.formatWithoutCurrency,
      selectedDate: selectedDateRef.value ?? preferredTimeStore.preferredDay,
      suggestedDate: cheapestItineraryDate.value,
      fallbackMonthDate: new Date(selectedMonth.value),
      hideAlternativeProductPrices: true,
    })
  );

  const calendar = computed<PeekPriceModalCalendar>(() => ({
    priceCalendarState: priceCalendarState.value,
    selectedMonth: selectedMonth.value,
    months: months.value,
  }));

  const cheapestItineraryDate = computed<Date | undefined>(() =>
    getDateOfCheapestItinerary(itineraryDatesRef.value)
  );

  const selectedItinerary = computed<DateWithItinerary | undefined>(() => {
    const selectedItinerary = itineraryDatesRef.value.find(
      (itinerary: DateWithItinerary) =>
        doesDateMatchAnyComparisonDate(itinerary.date, [
          selectedDateRef.value,
          preferredTimeStore.preferredDay,
          cheapestItineraryDate.value,
        ])
    );

    return selectedItinerary;
  });

  const itinerary = computed<PeekPriceModalItineraryData | undefined>(() => {
    if (
      ingredientIndexRef.value === undefined ||
      hotelCodeRef.value === undefined ||
      peekPriceRecipe.value === undefined
    ) {
      return;
    }

    const ingredientId =
      recipeStore.recipe[ingredientIndexRef.value]?.ingredientId;

    if (!ingredientId) {
      return;
    }

    const hotelStaticInfo = tripStore.getStaticInfo(
      hotelCodeRef.value,
      ingredientId
    );

    if (!hotelStaticInfo) {
      return;
    }

    const displayDate =
      selectedItinerary.value?.date ?? cheapestItineraryDate.value;

    const hotelAvail =
      selectedItinerary.value?.itinerary?.hotelOnlyItinerary[0];

    if (recipeStore.isSingleHotelRecipe) {
      return getPeekPriceModalHotelOnlyItinerary({
        displayDate,
        paxes: paxStore.getPaxesButtonSimpleText,
        hotelAvail,
        hotelStaticInfo,
        getImageUrl,
        formatMoney: globalCurrencyStore.formatMoney,
        getGuestRating,
      });
    }

    if (recipeStore.isFlightPlusHotelRecipe) {
      return getPeekPriceModalBookableTransportPlusHotelItinerary({
        displayDate,
        peekPriceRecipe: peekPriceRecipe.value,
        selectedItinerary: selectedItinerary.value,
        paxes: paxStore.getPaxesButtonSimpleText,
        hotelStaticInfo,
        type: PeekPriceModalItineraryType.FlightPlusHotel,
        getImageUrl,
        formatMoney: globalCurrencyStore.formatMoney,
        getGuestRating,
        getPortStaticInfo: tripStore.getPortStaticInfo,
        getCarriersStaticInfo: tripStore.getCarriersStaticInfo,
      });
    }

    if (recipeStore.isFerryPlusHotelRecipe) {
      return getPeekPriceModalBookableTransportPlusHotelItinerary({
        displayDate,
        peekPriceRecipe: peekPriceRecipe.value,
        selectedItinerary: selectedItinerary.value,
        paxes: paxStore.getPaxesButtonSimpleText,
        hotelStaticInfo,
        type: PeekPriceModalItineraryType.FerryPlusHotel,
        getImageUrl,
        formatMoney: globalCurrencyStore.formatMoney,
        getGuestRating,
        getPortStaticInfo: tripStore.getPortStaticInfo,
        getCarriersStaticInfo: tripStore.getCarriersStaticInfo,
      });
    }

    return getPeekPriceModalMultiIngredientItinerary({
      displayDate,
      peekPriceRecipe: peekPriceRecipe.value,
      selectedItinerary: selectedItinerary.value,
      paxes: paxStore.getPaxesButtonSimpleText,
      hotelStaticInfo,
      itineraryStaticInfo: itineraryStaticInfo.value,
      getImageUrl,
      formatMoney: globalCurrencyStore.formatMoney,
      getPortStaticInfo: tripStore.getPortStaticInfo,
      getCarriersStaticInfo: tripStore.getCarriersStaticInfo,
    });
  });

  // Watchers
  watch(selectedMonthISODateRef, () => {
    if (
      ingredientIndexRef.value !== undefined &&
      hotelCodeRef.value !== undefined
    ) {
      loadPrices(ingredientIndexRef.value, hotelCodeRef.value);
    }
  });

  return {
    // State
    // * it is considered private for this module
    // * it is exported here to to allow for inspection via pinia dev tools
    itineraryDatesRef: readonly(itineraryDatesRef),
    itineraryStaticInfo: readonly(itineraryStaticInfo),
    selectedMonthISODateRef: readonly(selectedMonthISODateRef),
    ingredientIndexRef: readonly(ingredientIndexRef),
    hotelCodeRef: readonly(hotelCodeRef),
    selectedDateRef: readonly(selectedDateRef),
    peekPriceRecipe: readonly(peekPriceRecipe),

    // Actions
    clean,
    confirm,
    loadPrices,
    preselectDate,
    selectMonth,

    // Getters
    itinerary,
    calendar,
  };
});
